import {
    vec3,
    wgslFn,
} from 'three/tsl';

// Shadow dithering — Bayer matrix dissolve at shadow boundaries
// Lit areas stay clean, deep shadows get solid tint,
// the transition zone between them gets Bayer-dithered stippling.
export const shadowDitherWGSL = wgslFn(/* wgsl */ `
fn shadowDitherFn(
    inputColor: vec4f,
    uvCoord: vec2f,
    resolution: vec2f,
    shadowTint: vec3f,
    shadowThreshold: f32,
    ditherEdgeWidth: f32,
    pixelSize: f32,
    tintStrength: f32
) -> vec4f {
    let bayerMatrix8x8 = array<f32, 64>(
        0.0/64.0, 48.0/64.0, 12.0/64.0, 60.0/64.0, 3.0/64.0, 51.0/64.0, 15.0/64.0, 63.0/64.0,
        32.0/64.0, 16.0/64.0, 44.0/64.0, 28.0/64.0, 35.0/64.0, 19.0/64.0, 47.0/64.0, 31.0/64.0,
        8.0/64.0, 56.0/64.0, 4.0/64.0, 52.0/64.0, 11.0/64.0, 59.0/64.0, 7.0/64.0, 55.0/64.0,
        40.0/64.0, 24.0/64.0, 36.0/64.0, 20.0/64.0, 43.0/64.0, 27.0/64.0, 39.0/64.0, 23.0/64.0,
        2.0/64.0, 50.0/64.0, 14.0/64.0, 62.0/64.0, 1.0/64.0, 49.0/64.0, 13.0/64.0, 61.0/64.0,
        34.0/64.0, 18.0/64.0, 46.0/64.0, 30.0/64.0, 33.0/64.0, 17.0/64.0, 45.0/64.0, 29.0/64.0,
        10.0/64.0, 58.0/64.0, 6.0/64.0, 54.0/64.0, 9.0/64.0, 57.0/64.0, 5.0/64.0, 53.0/64.0,
        42.0/64.0, 26.0/64.0, 38.0/64.0, 22.0/64.0, 41.0/64.0, 25.0/64.0, 37.0/64.0, 21.0/64.0
    );

    // Pixelate UV for chunky dither dots
    let normalizedPixelSize = pixelSize / resolution;
    let uvPixel = normalizedPixelSize * floor(uvCoord / normalizedPixelSize);

    // Luminance of the lit fragment (perceptual weights)
    let luminance = dot(inputColor.rgb, vec3f(0.2126, 0.7152, 0.0722));

    // Shadow amount: 0 = fully lit, 1 = deep shadow
    let shadowAmount = 1.0 - clamp(luminance, 0.0, 1.0);

    // Shadow zones
    let edgeStart = shadowThreshold;
    let edgeEnd = shadowThreshold + ditherEdgeWidth;

    // Bayer threshold lookup
    let x = i32(uvPixel.x * resolution.x) % 8;
    let y = i32(uvPixel.y * resolution.y) % 8;
    let bayer = bayerMatrix8x8[y * 8 + x];

    // Shadow-tinted color: multiply original by tint color
    let tintedColor = mix(inputColor.rgb, inputColor.rgb * shadowTint, tintStrength);

    // Determine final color based on shadow zone
    var result = inputColor.rgb;

    if (shadowAmount > edgeEnd) {
        // Deep shadow — solid tint
        result = tintedColor;
    } else if (shadowAmount > edgeStart) {
        // Dither transition zone
        let edgeProgress = (shadowAmount - edgeStart) / ditherEdgeWidth;

        // Bayer comparison: above threshold = tinted, below = original
        if (edgeProgress > bayer) {
            result = tintedColor;
        } else {
            result = inputColor.rgb;
        }
    }

    return vec4f(result, inputColor.a);
}
`);

export const shadowDitherConfig = {
    shadowTint: { x: 0.0, y: 0.0, z: 0.93 },
    shadowThreshold: 0.25,
    ditherEdgeWidth: 1.0,
    pixelSize: 2,
    tintStrength: 0.48,
};
